package com.bulut.mecitogullari.data.model

import kotlinx.serialization.Serializable

/** The only apartment types the contractor builds. */
val ApartmentTypes = listOf("1+1", "2+1", "3+1")

/** Agreement line for one apartment type: 10 × 1+1 at 85.000 ₺ each. */
@Serializable
data class WorkTypeAgreement(
    val type: String,
    val agreedCount: Double = 0.0,
    val unitPrice: Double = 0.0,
    val completedCount: Double = 0.0,
) {
    val agreedValue: Double get() = agreedCount * unitPrice
    val earnedValue: Double get() = completedCount * unitPrice
    val remainingCount: Double get() = (agreedCount - completedCount).coerceAtLeast(0.0)
}

/**
 * A craftsman working in exchange for apartments (iş karşılığı / takas):
 * e.g. the electrician wires 50 apartments and receives one apartment.
 */
@Serializable
data class Craftsman(
    val id: String,
    val name: String,
    /** Elektrik, Sıva, Fayans… */
    val trade: String = "",
    val phone: String = "",
    val notes: String = "",
    /** Müteahhidin bu ustadan istediği toplam fatura tutarı (0 = fatura istenmiyor). */
    val requiredInvoiceAmount: Double = 0.0,
    // Legacy single-type agreement (older records) — superseded by [workTypes].
    val agreedWorkCount: Double = 0.0,
    val workUnitLabel: String = "daire",
    val workUnitPrice: Double = 0.0,
    val completedWorkCount: Double = 0.0,
    /** Per apartment-type agreement: 1+1 / 2+1 / 3+1. */
    val workTypes: List<WorkTypeAgreement> = emptyList(),
    @Serializable(with = LongAsDoubleSerializer::class)
    val createdAtEpochMillis: Long,
) {
    /** New-style breakdown; legacy records are surfaced as one "daire" line. */
    val effectiveWorkTypes: List<WorkTypeAgreement>
        get() = workTypes.ifEmpty {
            if (agreedWorkCount > 0 || completedWorkCount > 0) {
                listOf(
                    WorkTypeAgreement(
                        type = workUnitLabel,
                        agreedCount = agreedWorkCount,
                        unitPrice = workUnitPrice,
                        completedCount = completedWorkCount,
                    )
                )
            } else {
                emptyList()
            }
        }

    val agreedTotalValue: Double get() = effectiveWorkTypes.sumOf { it.agreedValue }
    val earnedFromWork: Double get() = effectiveWorkTypes.sumOf { it.earnedValue }
    val totalAgreedCount: Double get() = effectiveWorkTypes.sumOf { it.agreedCount }
    val totalCompletedCount: Double get() = effectiveWorkTypes.sumOf { it.completedCount }
    val remainingWorkCount: Double get() = (totalAgreedCount - totalCompletedCount).coerceAtLeast(0.0)
}

@Serializable
enum class CraftsmanEntryType {
    /** Ekstra iş veya malzeme — ustanın alacağını artırır. */
    EXTRA_WORK,

    /** Müteahhitten nakit ödeme — ustanın alacağını azaltır. */
    CASH_PAYMENT,

    /** Ustanın kestiği fatura — bakiyeyi etkilemez, sadece fatura takibi. */
    INVOICE,
}

@Serializable
data class CraftsmanEntry(
    val id: String,
    val craftsmanId: String,
    val type: CraftsmanEntryType,
    @Serializable(with = LongAsDoubleSerializer::class)
    val dateEpochMillis: Long,
    val description: String = "",
    val amount: Double = 0.0,
)

/**
 * Craftsman account math. Positive net = craftsman is owed (alacaklı),
 * negative = craftsman owes (borçlu).
 */
data class CraftsmanBalance(
    val earnedFromWork: Double,
    val extras: Double,
    val cashPaid: Double,
    val unitsGivenValue: Double,
    val agreedTotalValue: Double,
    /** Fatura tracking — kept out of the cash math on purpose. */
    val requiredInvoice: Double = 0.0,
    val invoiced: Double = 0.0,
) {
    val currentNet: Double get() = earnedFromWork + extras - cashPaid - unitsGivenValue

    /** Net once the whole agreed work is finished. */
    val projectedNet: Double get() = agreedTotalValue + extras - cashPaid - unitsGivenValue

    val remainingInvoice: Double get() = (requiredInvoice - invoiced).coerceAtLeast(0.0)
}

fun craftsmanBalance(
    craftsman: Craftsman,
    entries: List<CraftsmanEntry>,
    unitsGiven: List<ApartmentUnit>,
): CraftsmanBalance = CraftsmanBalance(
    earnedFromWork = craftsman.earnedFromWork,
    extras = entries.filter { it.type == CraftsmanEntryType.EXTRA_WORK }.sumOf { it.amount },
    cashPaid = entries.filter { it.type == CraftsmanEntryType.CASH_PAYMENT }.sumOf { it.amount },
    unitsGivenValue = unitsGiven.sumOf { it.salePrice ?: 0.0 },
    agreedTotalValue = craftsman.agreedTotalValue,
    requiredInvoice = craftsman.requiredInvoiceAmount,
    invoiced = entries.filter { it.type == CraftsmanEntryType.INVOICE }.sumOf { it.amount },
)
